import { Phone, MapPin, Clock, Instagram, Facebook, Star, ExternalLink } from "lucide-react";
import { BRAND } from "@/data/products";
import { waLink } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/analytics";

export const Footer = () => {
  return (
    <footer id="contato" className="bg-[#F4F2EC] border-t border-[#E5E3DB]">
      {/* Bloco superior: mapa + contato */}
      <div className="container-wide pt-16 md:pt-20 pb-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="font-serif text-3xl md:text-4xl text-[#2D4A3E] leading-tight">Central de Coroas de Flores</p>
          <p className="overline mt-2">Rio de Janeiro · RJ</p>
          <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-md">
            Especialistas em coroas de flores para velórios, missas e cerimônias de homenagem em todos os
            cemitérios do município do Rio de Janeiro. Atendimento humanizado, 24 horas por dia.
          </p>

          <a
            href={waLink("Olá, gostaria de fazer um pedido de coroa de flores.")}
            target="_blank" rel="noreferrer"
            onClick={() => trackWhatsAppClick("footer")}
            className="btn-whatsapp mt-7 inline-flex"
            data-testid="footer-whatsapp-btn"
          >
            Falar agora pelo WhatsApp
          </a>

          <div className="mt-8">
            <p className="overline mb-4">Contato</p>
            <ul className="space-y-3 text-sm text-[#1C1C1C]">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-[#2D4A3E] mt-0.5" strokeWidth={1.5} />
                <a href={`tel:+${BRAND.phoneE164}`} className="hover:text-[#2D4A3E]">{BRAND.phoneDisplay}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#2D4A3E] mt-0.5" strokeWidth={1.5} />
                <span>R. Real Grandeza, 450 — Botafogo<br/>Rio de Janeiro · RJ · 22281-030</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-[#2D4A3E] mt-0.5" strokeWidth={1.5} />
                <span>{BRAND.hours}</span>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href={BRAND.social.instagram}
                target="_blank" rel="noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="h-10 w-10 rounded-full border border-[#E5E3DB] hover:border-[#2D4A3E] flex items-center justify-center transition-colors"
              >
                <Instagram className="h-4 w-4 text-[#2D4A3E]" strokeWidth={1.5} />
              </a>
              <a
                href={BRAND.social.facebook}
                target="_blank" rel="noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="h-10 w-10 rounded-full border border-[#E5E3DB] hover:border-[#2D4A3E] flex items-center justify-center transition-colors"
              >
                <Facebook className="h-4 w-4 text-[#2D4A3E]" strokeWidth={1.5} />
              </a>
              <a
                href={BRAND.social.google}
                target="_blank" rel="noreferrer"
                aria-label="Perfil no Google"
                data-testid="footer-google"
                className="inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full border border-[#E5E3DB] hover:border-[#2D4A3E] text-xs font-medium text-[#2D4A3E] transition-colors"
              >
                <Star className="h-4 w-4 fill-[#F5B400] text-[#F5B400]" strokeWidth={0} />
                5,0 · Google
              </a>
            </div>
          </div>
        </div>

        {/* Mapa Google */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <p className="overline">Onde estamos</p>
            <a
              href={BRAND.social.mapsLink}
              target="_blank" rel="noreferrer"
              data-testid="footer-maps-link"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2D4A3E] hover:text-[#1F352B]"
            >
              Traçar rota <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="relative aspect-[16/11] md:aspect-[16/9] rounded-2xl overflow-hidden border border-[#E5E3DB] bg-white">
            <iframe
              title="Localização Central de Coroa de Flores RJ — R. Real Grandeza, 450"
              src={BRAND.social.mapsEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
              allowFullScreen
            />
          </div>
          <p className="mt-3 text-xs text-[#5C5C5C]">
            R. Real Grandeza, 450 — Botafogo · Rio de Janeiro / RJ · CEP 22281-030
          </p>
        </div>
      </div>

      {/* Bloco inferior — navegação e créditos */}
      <div className="container-wide pb-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 pt-8 border-t border-[#E5E3DB]">
          <div>
            <p className="overline mb-3">Navegação</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#catalogo" className="text-[#1C1C1C] hover:text-[#2D4A3E]">Catálogo</a></li>
              <li><a href="#como-funciona" className="text-[#1C1C1C] hover:text-[#2D4A3E]">Como funciona</a></li>
              <li><a href="#cobertura" className="text-[#1C1C1C] hover:text-[#2D4A3E]">Cobertura</a></li>
              <li><a href="#faq" className="text-[#1C1C1C] hover:text-[#2D4A3E]">Dúvidas</a></li>
              <li><a href="#contato" className="text-[#1C1C1C] hover:text-[#2D4A3E]">Contato</a></li>
            </ul>
          </div>
          <div>
            <p className="overline mb-3">Redes sociais</p>
            <ul className="space-y-2 text-sm">
              <li>
                <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="text-[#1C1C1C] hover:text-[#2D4A3E] inline-flex items-center gap-2">
                  <Instagram className="h-3.5 w-3.5" strokeWidth={1.5} /> @central_de_coroa_de_flores
                </a>
              </li>
              <li>
                <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" className="text-[#1C1C1C] hover:text-[#2D4A3E] inline-flex items-center gap-2">
                  <Facebook className="h-3.5 w-3.5" strokeWidth={1.5} /> Rio de Janeiro Floricultura
                </a>
              </li>
              <li>
                <a href={BRAND.social.google} target="_blank" rel="noreferrer" className="text-[#1C1C1C] hover:text-[#2D4A3E] inline-flex items-center gap-2">
                  <Star className="h-3.5 w-3.5 fill-[#F5B400] text-[#F5B400]" strokeWidth={0} /> Perfil no Google · 5,0 ★
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="overline mb-3">Atendimento</p>
            <ul className="space-y-2 text-sm text-[#1C1C1C]">
              <li>{BRAND.hours}</li>
              <li>Entrega grátis</li>
              <li>Faixa personalizada grátis</li>
              <li>PIX · Cartão · Dinheiro</li>
            </ul>
          </div>
          <div>
            <p className="overline mb-3">Área de atendimento</p>
            <p className="text-sm text-[#1C1C1C] leading-relaxed">
              Todos os cemitérios, capelas e velórios do município do Rio de Janeiro — inclusive
              São João Batista, Caju, Memorial do Carmo, Inhaúma, Catumbi, Irajá e Sulacap.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5E3DB]">
        <div className="container-wide py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5C5C5C]">
          <p>© {new Date().getFullYear()} Central de Coroa de Flores RJ · Todos os direitos reservados.</p>
          <p>Atendimento 24 horas · {BRAND.phoneDisplay}</p>
        </div>
      </div>
    </footer>
  );
};
